// Organization > People: everyone who works here, the role they hold and the
// part of the network that role lets them see.
//
// Not only logins — a workshop stakeholder or a service advisor is a person
// too, and most of them have no email. All three are listed, and all three
// can be edited; what differs is what there is to edit.
//
// Adding someone creates the login only. They set their own password at
// sign-up, which is what turns Invited into Active — the same path every
// stakeholder and service advisor already takes.

import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { useTenantOrganization } from "../../lib/api";
import { filterPeople, indexOrg, personRemovalNote, personRows } from "../../lib/organization";
import PersonDialog from "./PersonDialog";
import { usePersonRemoval } from "./useOrgEditors";
import {
  Cell,
  ConfirmDelete,
  EmptyState,
  ErrorState,
  FilterSelect,
  Loading,
  PageIntro,
  SearchBox,
  StatusChip,
  Table,
  Toolbar,
} from "./OrgKit";

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

export default function People({ tenantId, readOnly = false }) {
  const { data, isLoading, isError, error, refetch } = useTenantOrganization(tenantId);
  const [filters, setFilters] = useState({
    query: "",
    role: "all",
    dealer: "all",
    workshop: "all",
    status: "all",
  });
  const [adding, setAdding] = useState(false);
  // The person being edited, or null for a new one. Editing exists chiefly so
  // someone can be moved off a region or workshop before it is deleted.
  const [editing, setEditing] = useState(null);

  const ix = useMemo(() => indexOrg(data), [data]);
  const { deleting, setDeleting, askRemove, confirmRemove, pending } = usePersonRemoval(tenantId, ix);
  const rows = useMemo(() => personRows(ix), [ix]);
  const shown = useMemo(() => filterPeople(rows, filters), [rows, filters]);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const openAdd = () => {
    setEditing(null);
    setAdding(true);
  };

  const openEdit = (person) => {
    setEditing(person);
    setAdding(true);
  };

  // Built from the rows on screen, not from the whole org: a filter offering
  // a dealer that nobody here works for only ever empties the table.
  const optionsFrom = (pick, anyLabel) => [
    { value: "all", label: anyLabel },
    ...[...new Set(rows.map(pick).filter(Boolean))].sort().map((v) => ({ value: v, label: v })),
  ];

  const roleFilterOptions = optionsFrom((r) => r.roleLabel, "Any role");
  const dealerFilterOptions = optionsFrom((r) => r.dealerName, "Any dealer");
  const workshopFilterOptions = optionsFrom((r) => r.workshopLocation, "Any workshop");

  return (
    <>
      <PageIntro
        title="People"
        actionLabel={readOnly ? null : "Add person"}
        onAction={openAdd}
      />

      {rows.length > 0 && (
        <Toolbar count={`${shown.length} of ${rows.length}`}>
          <SearchBox
            value={filters.query}
            onChange={(v) => set({ query: v })}
            placeholder="Search people"
            data-testid="people-search"
          />
          <FilterSelect
            value={filters.role}
            onChange={(v) => set({ role: v })}
            options={roleFilterOptions}
            aria-label="Role"
          />
          <FilterSelect
            value={filters.dealer}
            onChange={(v) => set({ dealer: v })}
            options={dealerFilterOptions}
            aria-label="Dealer"
          />
          <FilterSelect
            value={filters.workshop}
            onChange={(v) => set({ workshop: v })}
            options={workshopFilterOptions}
            aria-label="Workshop"
          />
          <FilterSelect
            value={filters.status}
            onChange={(v) => set({ status: v })}
            options={[
              { value: "all", label: "Any status" },
              { value: "active", label: "Active" },
              { value: "invited", label: "Invited" },
              { value: "no_login", label: "No login" },
            ]}
            aria-label="Status"
          />
        </Toolbar>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="Nobody has a login yet"
          body="Add the first one."
          actionLabel={readOnly ? null : "Add person"}
          onAction={openAdd}
        />
      ) : shown.length === 0 ? (
        <EmptyState title="Nobody matches those filters" body="Clear a filter, or add the person who should be here." />
      ) : (
        <Table columns={["Name", "Role", "Organization", "Phone", "Access", "Status"]}>
          {shown.map((row) => (
            <tr key={row.id} data-testid="person-row">
              <Cell>
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EFF5FC] text-[12px] font-semibold text-[#1F5596]">
                    {initials(row.name)}
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-[14px] font-medium text-[#111827]">{row.name}</span>
                    <span className="truncate text-[13px] text-[#6B7280]">{row.email}</span>
                  </span>
                </div>
              </Cell>
              <Cell>{row.roleLabel}</Cell>
              <Cell className="text-[13px]">{row.org}</Cell>
              <Cell className="text-[13px]">{row.number || "—"}</Cell>
              <Cell className="text-[13px]">{row.access}</Cell>
              <Cell>
                <StatusChip status={row.status} />
              </Cell>
              <Cell className="whitespace-nowrap text-right">
                {!readOnly && (
                  <>
                    <button
                      type="button"
                      onClick={() => openEdit((ix.people || []).find((p) => p.id === row.id))}
                      aria-label={`Edit ${row.name}`}
                      className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#111827]"
                    >
                      <Pencil size={15} />
                    </button>
                    {/* Everyone can be removed, login or not; the prefixed id
                        tells the API which table the row is in. */}
                    <button
                        type="button"
                        onClick={() => askRemove(row.id, row.hasLogin)}
                        aria-label={`Remove ${row.name}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
                        </svg>
                      </button>
                  </>
                )}
              </Cell>
            </tr>
          ))}
        </Table>
      )}

      <PersonDialog
        open={adding}
        onOpenChange={setAdding}
        tenantId={tenantId}
        ix={ix}
        person={editing}
      />

      <ConfirmDelete
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        name={deleting?.name || ""}
        body={personRemovalNote(deleting?.hasLogin)}
        onConfirm={confirmRemove}
        pending={pending}
      />
    </>
  );
}
